'use client'

import { Checkbox as HeroCheckbox, CheckboxProps as HeroCheckboxProps } from '@heroui/react'
import { forwardRef } from 'react'
import { Icon } from '@/components/ui/icon'
import type { IconName } from '@/config/icons'

export interface CheckboxProps extends HeroCheckboxProps {
  /** Inline error to display under the checkbox. */
  error?: string
  /** Helper text shown when there's no error. */
  helperText?: string
  /** Optional icon rendered alongside the label. */
  icon?: IconName
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ error, helperText, icon, children, className, ...props }, ref) => {
    return (
      <div className="flex flex-col gap-1">
        <HeroCheckbox ref={ref as never} className={className} {...props}>
          {/* Checkbox.Content is the clickable <label>: it carries the real <input>
              and all press handling. Checkbox.Control MUST be nested INSIDE it. As a
              sibling (the previous shape here) the box still rendered, but sat outside
              the label, so NO mouse or touch click could tick it -- only keyboard
              Space. On /pay that left the terms box uncheckable and the "Pay" button
              permanently disabled: customers could not pay at all. Same failure mode as
              Switch.Control outside Switch.Content. */}
          <HeroCheckbox.Content className="flex flex-row items-center gap-2 min-h-11">
            {/* An unchecked box ships with no border of its own, so it renders invisible
                and the control reads as a bare label. The border gives it an empty state;
                the checked fill paints over it. */}
            <HeroCheckbox.Control className="size-4 shrink-0 rounded-[4px] border border-foreground/30">
              <HeroCheckbox.Indicator />
            </HeroCheckbox.Control>
            <span className="flex items-center gap-1.5">
              {icon && <Icon icon={icon} className="text-base" />}
              {children as React.ReactNode}
            </span>
          </HeroCheckbox.Content>
        </HeroCheckbox>
        {helperText && !error && (
          <span className="text-sm text-foreground/60 ml-7">{helperText}</span>
        )}
        {error && <span className="text-sm text-danger ml-7">{error}</span>}
      </div>
    )
  }
)

Checkbox.displayName = 'Checkbox'

export default Checkbox
